<template>
  <div>
    <!-- Toolbar -->
    <div class="d-flex justify-content-end mb-3">
      <button class="btn btn-sm btn-outline-secondary" @click="openModal">
        <i class="fa-solid fa-plus me-1"></i>
        Nova conta bancária
      </button>
    </div>

    <!-- Lista de contas bancárias -->
    <div v-if="item.bank_account && item.bank_account.length">
      <h6 class="text-muted mb-3">
        Contas Bancárias
        <span class="badge bg-primary-soft ms-2">{{
          item.bank_account.length
        }}</span>
      </h6>

      <div
        v-for="(account, index) in item.bank_account"
        :key="index"
        class="card mb-3 shadow-sm"
      >
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">
              <i class="fa-solid fa-building-columns me-2 text-muted"></i>
              Conta #{{ index + 1 }}
            </h6>
            <div>
              <span class="badge bg-info me-2">
                {{
                  item.business_type === "individual" ? "Conta PF" : "Conta PJ"
                }}
              </span>
              <span v-if="index === 0" class="badge bg-success">Principal</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <TextInput
                :readOnly="true"
                label="Titular da conta"
                :value="`${account.legal_name}`"
              />
            </div>
            <div class="col-12 col-xl-6">
              <TextInput
                :readOnly="true"
                label="Banco"
                :value="`${account.bank_code}`"
              />
            </div>
            <div class="col-12 col-xl-6">
              <TextInput
                :readOnly="true"
                label="Agência"
                :value="`${account.agencia}${
                  account.agencia_dv ? '-' + account.agencia_dv : ''
                }`"
              />
            </div>
            <div class="col-12 col-xl-6">
              <TextInput
                :readOnly="true"
                label="Conta"
                :value="`${account.conta}${
                  account.conta_dv ? '-' + account.conta_dv : ''
                }`"
              />
            </div>
            <div class="col-12 col-xl-6" v-if="account.document_number">
              <TextInput
                :readOnly="true"
                label="CPF"
                :value="`${account.document_number}`"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="alert alert-warning">
      Nenhuma conta bancária cadastrada.
    </div>

    <!-- Modal: Nova conta bancária -->
    <div
      v-if="showModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fa-solid fa-building-columns me-2 text-primary"></i>
              Nova conta bancária
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <!-- Busca de banco -->
            <div class="form-group mb-3 position-relative">
              <label class="form-label text-muted">
                Banco <i class="text-danger">*</i>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="bankSearchQuery"
                @input="searchBanks"
                placeholder="Digite para buscar o banco..."
                :class="{ 'is-invalid': $v.form.bank_code.$error }"
              />
              <div v-if="bankSearchResults.length > 0" class="bank-search-results">
                <div
                  v-for="bank in bankSearchResults"
                  :key="bank.code"
                  class="bank-search-item"
                  @click="selectBank(bank)"
                >
                  {{ bank.code }} - {{ bank.name }}
                </div>
              </div>
              <div v-if="selectedBank" class="mt-2">
                <span class="badge bg-primary">
                  <i class="fa-solid fa-building-columns me-2"></i>
                  {{ selectedBank.code }} - {{ selectedBank.name }}
                </span>
              </div>
              <div class="invalid-feedback">Selecione um banco</div>
            </div>

            <TextInput
              label="Nome do titular"
              :model="$v.form.legal_name"
              placeholder="Nome completo do titular"
              :required="true"
              errorMsg="Nome do titular é obrigatório"
            />

            <div class="row">
              <div class="col-8">
                <TextInput
                  label="Agência"
                  :model="$v.form.agencia"
                  placeholder="0000"
                  :required="true"
                  errorMsg="Agência é obrigatória"
                />
              </div>
              <div class="col-4">
                <TextInput
                  label="Dígito"
                  :model="$v.form.agencia_dv"
                  placeholder="0"
                  :required="false"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-8">
                <TextInput
                  label="Conta"
                  :model="$v.form.conta"
                  placeholder="00000-0"
                  :required="true"
                  errorMsg="Conta é obrigatória"
                />
              </div>
              <div class="col-4">
                <TextInput
                  label="Dígito"
                  :model="$v.form.conta_dv"
                  placeholder="0"
                  :required="false"
                />
              </div>
            </div>

            <p v-if="errorMessage" class="text-center text-danger mt-3">
              {{ errorMessage }}
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="$v.form.$invalid || submitting"
              @click="handleSubmit"
            >
              <span v-if="submitting">
                <i class="fa-solid fa-spinner fa-spin me-1"></i> Criando...
              </span>
              <span v-else>
                <i class="fa-solid fa-check me-1"></i> Criar conta bancária
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";

export default {
  name: "PsiDetailBankInfo",
  props: {
    item: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showModal: false,
      form: {
        bank_code: "",
        agencia: "",
        agencia_dv: "",
        conta: "",
        conta_dv: "",
        legal_name: this.item?.name || "",
      },
      bankSearchQuery: "",
      bankSearchResults: [],
      selectedBank: null,
      submitting: false,
      errorMessage: "",
      searchTimeout: null,
    };
  },
  mounted() {
    this.$eventBus.$on("psiDetail:openNewBankAccount", this.openModal);
  },
  beforeDestroy() {
    this.$eventBus.$off("psiDetail:openNewBankAccount", this.openModal);
  },
  validations: {
    form: {
      bank_code: { required },
      agencia: { required },
      agencia_dv: {},
      conta: { required },
      conta_dv: {},
      legal_name: { required },
    },
  },
  methods: {
    openModal() {
      this.resetForm();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    searchBanks() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (this.bankSearchQuery.length < 2) {
        this.bankSearchResults = [];
        return;
      }

      this.searchTimeout = setTimeout(async () => {
        try {
          const response = await this.$bankAccountService.listBankCodes({
            q: this.bankSearchQuery,
          });
          this.bankSearchResults = response.data || [];
        } catch (error) {
          console.error("Erro ao buscar bancos:", error);
          this.bankSearchResults = [];
        }
      }, 300);
    },
    selectBank(bank) {
      this.selectedBank = bank;
      this.form.bank_code = bank.code;
      this.bankSearchQuery = "";
      this.bankSearchResults = [];
      this.$v.form.bank_code.$touch();
    },
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.submitting = true;
      this.errorMessage = "";

      try {
        const payload = {
          ...this.form,
          user_id: this.item.id,
        };
        await this.$bankAccountService.create(payload);

        this.$eventBus.$emit("showToast", {
          title: "Conta bancária criada",
          msg: "A conta bancária foi criada com sucesso.",
        });

        this.showModal = false;
        this.$emit("accountCreated");
        this.resetForm();
      } catch (error) {
        this.errorMessage = error.message || "Erro ao criar conta bancária";
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.form = {
        bank_code: "",
        agencia: "",
        agencia_dv: "",
        conta: "",
        conta_dv: "",
        legal_name: this.item?.name || "",
      };
      this.bankSearchQuery = "";
      this.bankSearchResults = [];
      this.selectedBank = null;
      this.errorMessage = "";
      this.$v.$reset();
    },
  },
};
</script>

<style scoped>
.card {
  border: 1px solid #e9ecef;
  transition: all 0.15s ease;
}

.card:hover {
  border-color: #dee2e6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.bank-search-results {
  position: absolute;
  z-index: 1060;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.bank-search-item {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.bank-search-item:hover {
  background-color: #f8f9fa;
}

.bank-search-item:last-child {
  border-bottom: none;
}
</style>
